import Box, { type BoxProps } from '@mui/material/Box';
import type { ReactNode } from 'react';
import { sparkPaths } from '@/shared/lib';
import { fonts, inkFor, radius } from '@/shared/theme';
import { Icon, type IconName } from './icon';

/**
 * Direction of a change and whether it is good news: revenue up is good, no-shows up are bad.
 */
export type DeltaTone = 'up-good' | 'down-good' | 'up-bad' | 'down-bad' | 'flat';

const TONE_COLOR: Record<DeltaTone, string> = {
  'up-good': 'md.okInk',
  'down-good': 'md.okInk',
  'up-bad': 'md.badInk',
  'down-bad': 'md.badInk',
  flat: 'md.faint',
};

/** Arrow (or dot when unchanged) coloured by whether the change is good, not by its direction. */
export function TrendMark({ tone }: { tone: DeltaTone }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{ display: 'inline-flex', width: 14, justifyContent: 'center', color: TONE_COLOR[tone] }}
    >
      {tone === 'flat' ? (
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: 'currentColor' }}
        />
      ) : (
        <Icon name={tone.startsWith('up') ? 'sortUp' : 'sortDown'} size={14} />
      )}
    </Box>
  );
}

/** Rounded ▲/▼ change pill ("deltaPill" in the prototype). */
export function DeltaPill({
  text,
  tone,
  title,
}: {
  text: string;
  tone: DeltaTone;
  title?: string;
}) {
  const good = tone === 'up-good' || tone === 'down-good';
  const flat = tone === 'flat';
  const up = tone.startsWith('up');
  return (
    <Box
      component="span"
      title={title}
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        p: '4px 9px',
        borderRadius: radius.control,
        whiteSpace: 'nowrap',
        fontFamily: fonts.head,
        fontSize: 11.5,
        fontWeight: 800,
        fontVariantNumeric: 'tabular-nums',
        color: flat
          ? theme.vars.palette.md.muted
          : good
            ? theme.vars.palette.md.okInk
            : theme.vars.palette.md.badInk,
        background: flat
          ? theme.vars.palette.md.panel3
          : good
            ? theme.vars.palette.md.ok050
            : theme.vars.palette.md.bad050,
      })}
    >
      <Box component="span" aria-hidden sx={{ fontSize: 8, lineHeight: 1 }}>
        {flat ? '●' : up ? '▲' : '▼'}
      </Box>
      {text}
    </Box>
  );
}

/** Tinted square with an icon (KPI cards, work lists). */
export function IconChip({ icon, hue, size = 44 }: { icon: IconName; hue: string; size?: number }) {
  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        flex: 'none',
        borderRadius: radius.control,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: hue,
        background: `color-mix(in srgb, ${hue} 15%, transparent)`,
      }}
    >
      <Icon name={icon} size={Math.round(size * 0.47)} />
    </Box>
  );
}

/** Small colored dot used for locations and statuses. */
export function Dot({ color, size = 8 }: { color: string; size?: number }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{ width: size, height: size, borderRadius: 99, flex: 'none', background: color }}
    />
  );
}

/** Horizontal meter (target attainment, shares). `value` is 0–100. */
export function Meter({
  value,
  color,
  height = 7,
  label,
}: {
  value: number;
  color: string;
  height?: number;
  label?: string;
}) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <Box
      role="meter"
      aria-label={label}
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      sx={(theme) => ({
        height,
        borderRadius: radius.xs,
        overflow: 'hidden',
        background: theme.vars.palette.md.panel3,
      })}
    >
      <Box
        sx={{ height: '100%', width: `${clamped}%`, background: color, transition: 'width .22s' }}
      />
    </Box>
  );
}

export interface StackSegment {
  key: string;
  value: number;
  color: string;
  title: string;
}

/**
 * Horizontal stacked bar on a track. Segments are shares of `total`; `fill` (0–100) scales the
 * whole stack, e.g. relative to the largest row of a ranking.
 */
export function StackedBar({
  segments,
  total,
  fill = 100,
  height = 12,
  label,
}: {
  segments: StackSegment[];
  total: number;
  fill?: number;
  height?: number;
  label?: string;
}) {
  return (
    <Box
      role="img"
      aria-label={label}
      sx={(theme) => ({
        height,
        borderRadius: radius.xs,
        overflow: 'hidden',
        background: theme.vars.palette.md.panel3,
        minWidth: 0,
      })}
    >
      <Box sx={{ display: 'flex', height: '100%', width: `${Math.max(0, Math.min(100, fill))}%` }}>
        {segments.map((s) => (
          <Box
            key={s.key}
            title={s.title}
            sx={{
              height: '100%',
              width: `${total > 0 ? Math.max(0, (s.value / total) * 100) : 0}%`,
              background: s.color,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

/** Tiny smoothed trend line with optional area fill. */
export function Sparkline({
  series,
  color,
  width = 120,
  height = 30,
  fill = true,
  sx,
}: {
  series: number[];
  color: string;
  width?: number;
  height?: number;
  fill?: boolean;
  sx?: BoxProps['sx'];
}) {
  const { line, area } = sparkPaths(series, width, height);
  if (!line) return null;
  return (
    <Box
      component="svg"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden
      sx={[{ display: 'block', height, width: '100%' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {fill && <path d={area} fill={color} fillOpacity={0.13} />}
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={fill ? 2 : 1.8}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </Box>
  );
}

/** Legend entry: colored swatch + label. */
export function LegendItem({
  color,
  label,
  variant = 'square',
}: {
  color: string;
  label: ReactNode;
  variant?: 'square' | 'line' | 'dashed';
}) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        fontSize: 12,
        color: 'text.secondary',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {variant === 'square' ? (
        <Box
          component="span"
          sx={{ width: 9, height: 9, borderRadius: radius.sm, flex: 'none', background: color }}
        />
      ) : (
        <Box
          component="span"
          sx={{
            width: 18,
            flex: 'none',
            borderTop: `2px ${variant === 'dashed' ? 'dashed' : 'solid'} ${color}`,
            opacity: 0.8,
          }}
        />
      )}
      {label}
    </Box>
  );
}

/** Tinted pill label for a role, status or state ("badge" in the prototype). */
export function Tag({ label, hue, title }: { label: ReactNode; hue: string; title?: string }) {
  return (
    <Box
      component="span"
      title={title}
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        px: '9px',
        py: '3px',
        borderRadius: radius.control,
        whiteSpace: 'nowrap',
        fontSize: 11,
        fontWeight: 700,
        color: inkFor(theme, hue),
        background: `color-mix(in srgb, ${hue} 14%, transparent)`,
      })}
    >
      {label}
    </Box>
  );
}
